import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { message } from 'antd';
import { postAuthenticatedRequest } from '../../../utils/api/requests/request';

const WorkflowContext = createContext();

export const WorkflowProvider = ({ children }) => {
    // Simple state management
    const [processInstances, setProcessInstances] = useState({});
    const [instanceStates, setInstanceStates] = useState({});
    
    // Use refs for data that shouldn't trigger re-renders
    const lastRequestsRef = useRef({
        instances: null,
        instanceState: null
    });
    
    // Track pending requests to prevent duplicates
    const pendingRequestsRef = useRef(new Map());

    // Loading states
    const [loading, setLoading] = useState({
        instances: false,
        instanceState: false,
        definition: false
    });

    // ==================== API FUNCTIONS ====================

    // Get process instances directly
    const getProcessInstances = useCallback(async (entityId, entityReferenceId, page = 1, pageSize = 10, forceRefresh = false, shouldShowClosedProcess= false) => {
        // Create unique key for this request
        const requestKey = `${entityReferenceId}-${entityId}-${page}-${pageSize}`;

        // Check if this request is already pending
        if (pendingRequestsRef.current.has(requestKey)) {
            return pendingRequestsRef.current.get(requestKey);
        }

        // Check if this is the same as last request and not forcing refresh
        if (!forceRefresh && lastRequestsRef.current.instances === requestKey) {
            // Return existing data if available
            const existingData = processInstances[requestKey];
            if (existingData) {
                return existingData;
            }
        }

        try {
            setLoading(prev => ({ ...prev, instances: true }));
            
            // Create a promise for this request
            const requestPromise = (async () => {
                // Update last request
                lastRequestsRef.current.instances = requestKey;

                const data = {
                    entityReferenceId: entityReferenceId,
                    entity: entityId,
                    pageSize,
                    shouldShowClosedProcess,
                    pageIndex: page - 1,
                };

                const response = await postAuthenticatedRequest(
                    "GetProcessInstanceListQuery",
                    data
                );

                if (response?.ok && response?.outData?.isSuccessful) {
                    // Extract instances from response.outData.data.instances
                    const responseData = response.outData.data?.instances || [];
                    const totalRows = response.outData.data?.count || response.outData.totalRows || 0;

                    const result = {
                        instances: responseData,
                        totalRows,
                        page,
                        pageSize,
                        _timestamp: Date.now(),
                        requestKey,
                        responseData: response.outData.data // Store full response data if needed
                    };

                    // Store directly in state
                    setProcessInstances(prev => ({
                        ...prev,
                        [requestKey]: result
                    }));

                    return result;
                } else {
                    throw new Error(response?.outData?.message ?? "Failed to fetch process instances");
                }
            })();

            // Store the promise
            pendingRequestsRef.current.set(requestKey, requestPromise);

            const result = await requestPromise;
            return result;
        } catch (error) {
            console.error('Error fetching process instances:', error);
            message.error(error.message);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, instances: false }));
            // Remove from pending requests
            pendingRequestsRef.current.delete(requestKey);
        }
    }, [processInstances]);

    // Get process instance state directly
    const getProcessInstanceState = useCallback(async (instanceGuid, forceRefresh = false) => {
        // Check if this request is already pending
        if (pendingRequestsRef.current.has(instanceGuid)) {
            return pendingRequestsRef.current.get(instanceGuid);
        }

        // Check cache (with timestamp for freshness)
        const cachedData = instanceStates[instanceGuid];
        const cacheTimestamp = cachedData?._timestamp;
        const now = Date.now();
        
        if (!forceRefresh && cachedData && cacheTimestamp && (now - cacheTimestamp) < 30000) { // 30 seconds cache
            return cachedData;
        }

        try {
            setLoading(prev => ({ ...prev, instanceState: true }));

            // Create a promise for this request
            const requestPromise = (async () => {
                // Update last request
                lastRequestsRef.current.instanceState = instanceGuid;

                const data = { instanceGuid };
                const response = await postAuthenticatedRequest(
                    "GetProcessInstanceStateCommand",
                    data
                );

                if (response?.ok && response?.outData?.isSuccessful) {
                    // The instance state is in response.outData.data
                    const instanceData = response.outData.data;
                    
                    const result = {
                        ...instanceData,
                        _timestamp: Date.now(), // Add timestamp for caching
                        instanceGuid // Store the instanceGuid in the data
                    };

                    // Update state
                    setInstanceStates(prev => ({
                        ...prev,
                        [instanceGuid]: result
                    }));

                    return result;
                } else {
                    throw new Error(response?.outData?.message ?? "Failed to fetch instance state");
                }
            })();

            // Store the promise
            pendingRequestsRef.current.set(instanceGuid, requestPromise);

            const result = await requestPromise;
            return result;
        } catch (error) {
            console.error('Error fetching instance state:', error);
            message.error(error.message);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, instanceState: false }));
            // Remove from pending requests
            pendingRequestsRef.current.delete(instanceGuid);
        }
    }, [instanceStates]);

    // Get workflow details for entity
    const getWorkflowDetailsForEntity = useCallback(async (entityId, entityReferenceId, forceRefresh = false) => {
        try {
            // First get instances
            const instances = await getProcessInstances(entityId, entityReferenceId, 1, 5, forceRefresh);
            
            if (!instances?.instances?.length) {
                return null;
            }

            // Get details for the first instance
            const firstInstance = instances.instances[0];
            if (firstInstance?.instanceGuid) {
                const instanceState = await getProcessInstanceState(firstInstance.instanceGuid, forceRefresh);
                return {
                    ...instanceState,
                    instanceGuid: firstInstance.instanceGuid,
                    processInstanceId: firstInstance.processInstanceId
                };
            }
            
            return null;
        } catch (error) {
            console.error('Error getting workflow details for entity:', error);
            return null;
        }
    }, [getProcessInstances, getProcessInstanceState]);

    // Clear specific data
    const clearData = (dataType, key = null) => {
        switch (dataType) {
            case 'instances':
                if (key) {
                    setProcessInstances(prev => {
                        const newData = { ...prev };
                        delete newData[key];
                        return newData;
                    });
                } else {
                    setProcessInstances({});
                }
                break;
            case 'states':
                if (key) {
                    setInstanceStates(prev => {
                        const newData = { ...prev };
                        delete newData[key];
                        return newData;
                    });
                } else {
                    setInstanceStates({});
                }
                break;
            case 'lastRequests':
                lastRequestsRef.current = {
                    instances: null,
                    instanceState: null
                };
                break;
            case 'all':
                setProcessInstances({});
                setInstanceStates({});
                lastRequestsRef.current = {
                    instances: null,
                    instanceState: null
                };
                pendingRequestsRef.current.clear();
                break;
        }
    };

    // Refresh specific data
    const refreshData = async (dataType, identifier) => {
        switch (dataType) {
            case 'instances':
                const [entityReferenceId, entityId, page, pageSize] = identifier.split('-');
                return await getProcessInstances(entityId, entityReferenceId, parseInt(page), parseInt(pageSize), true);
            case 'state':
                return await getProcessInstanceState(identifier, true);
            case 'entityWorkflow':
                const [entityIdParam, entityReferenceIdParam] = identifier.split('|');
                return await getWorkflowDetailsForEntity(entityIdParam, entityReferenceIdParam, true);
        }
    };

    const value = {
        // Data
        loading,
        processInstances,
        instanceStates,

        // Methods
        getProcessInstances,
        getProcessInstanceState,
        getWorkflowDetailsForEntity,

        // Data management
        clearData,
        refreshData,
    };

    return (
        <WorkflowContext.Provider value={value}>
            {children}
        </WorkflowContext.Provider>
    );
};

export const useWorkflow = () => {
    const context = useContext(WorkflowContext);
    if (!context) {
        throw new Error('useWorkflow must be used within a WorkflowProvider');
    }
    return context;
};